import { useCallback, useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import * as memberPreferenceService from '@/supabase/memberpreference/service/memberPreferenceService';
import { logError } from '@/lib/sentry';
import type { ShippingExcelSort } from '@/utils/shippingRowSort';

interface UseMemberPreferenceProps {
  user: User | null;
}

interface UseMemberPreferenceResult {
  /** 결제수단 기록 opt-in 값. 로딩 전/조회 실패/비로그인 = false (소비처 fail-closed) */
  recordPaymentMethod: boolean;
  /** 설정 화면용 상태. 'error'면 토글을 OFF로 렌더하지 말 것(덮어쓰기 방지) */
  preferenceStatus: 'loading' | 'ready' | 'error';
  /** opt-in 저장 (옵티미스틱 + 실패 시 원복). 성공 여부 반환 */
  setRecordPaymentMethod: (value: boolean) => Promise<boolean>;
  /** 배송엑셀 정렬 기준. 로딩 전/조회 실패/비로그인 = 'entry' (기존 동작) */
  shippingExcelSort: ShippingExcelSort;
  /** 정렬 기준 저장 (옵티미스틱 + 실패 시 원복). 성공 여부 반환 */
  setShippingExcelSort: (value: ShippingExcelSort) => Promise<boolean>;
}

/**
 * member_preference 도메인 훅.
 * 마운트당 1회 조회. 소비처(/order·TodaySales)는 recordPaymentMethod만 읽으면 fail-closed,
 * 설정 화면(MyPage)은 preferenceStatus로 로딩/에러를 구분해 렌더한다.
 */
export function useMemberPreference({ user }: UseMemberPreferenceProps): UseMemberPreferenceResult {
  const [recordPaymentMethod, setLocalValue] = useState(false);
  const [preferenceStatus, setPreferenceStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const saveSeqRef = useRef(0);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  // 정렬 기준은 별도 컬럼이라 seq/chain을 분리한다 — 공유 seq면 한쪽 저장이 다른 쪽 실패 원복을 막는다.
  const [shippingExcelSort, setLocalShippingSort] = useState<ShippingExcelSort>('entry');
  const sortSaveSeqRef = useRef(0);
  const sortSaveChainRef = useRef<Promise<void>>(Promise.resolve());
  const sortUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    // 계정 전환 시에만 기본값으로 리셋 — 같은 계정의 user 객체 교체(SIGNED_IN 재발행)에서
    // 리셋하면 재조회 동안 표가 입력 순서로 깜빡인다.
    const userId = user?.id ?? null;
    if (sortUserIdRef.current !== userId) {
      sortUserIdRef.current = userId;
      sortSaveSeqRef.current += 1; // 이전 계정의 진행 중 저장이 실패해도 새 계정 값을 원복하지 않게
      setLocalShippingSort('entry');
    }
    if (!user) {
      setLocalValue(false);
      setPreferenceStatus('ready');
      return;
    }
    setPreferenceStatus('loading');
    memberPreferenceService.getOwnPreference()
      .then(pref => {
        if (!isMounted) return;
        setLocalValue(pref?.record_payment_method ?? false);
        setLocalShippingSort(pref?.shipping_excel_sort === 'settlement' ? 'settlement' : 'entry');
        setPreferenceStatus('ready');
      })
      .catch(err => {
        if (!isMounted) return;
        logError('[useMemberPreference] preference 조회 실패:', err);
        setLocalValue(false);
        setPreferenceStatus('error');
      });
    return () => { isMounted = false; };
  }, [user]);

  const setRecordPaymentMethod = useCallback(async (value: boolean): Promise<boolean> => {
    if (!user) return false;
    if (preferenceStatus !== 'ready') return false; // 로드 미완료/에러 중 저장 금지 (stale fetch가 옵티미스틱 값을 덮는 race 차단)
    const prevValue = recordPaymentMethod;
    const seq = ++saveSeqRef.current;
    setLocalValue(value);
    // 저장 직렬화: 호출 순서 = 서버 기록 순서 (역전 시 서버에 옛 값이 남는 race 차단)
    const attempt = saveChainRef.current
      .catch(() => {})
      .then(() => memberPreferenceService.setRecordPaymentMethod(user.id, value));
    saveChainRef.current = attempt.then(() => undefined, () => undefined);
    try {
      await attempt;
      return true;
    } catch (err) {
      logError('[useMemberPreference] preference 저장 실패:', err);
      if (seq === saveSeqRef.current) {
        setLocalValue(prevValue);
      }
      return false;
    }
  }, [user, preferenceStatus, recordPaymentMethod]);

  const setShippingExcelSort = useCallback(async (value: ShippingExcelSort): Promise<boolean> => {
    if (!user) return false;
    if (preferenceStatus !== 'ready') return false;
    const requestUserId = user.id;
    const prevValue = shippingExcelSort;
    const seq = ++sortSaveSeqRef.current;
    setLocalShippingSort(value);
    const attempt = sortSaveChainRef.current
      .catch(() => {})
      .then(() => memberPreferenceService.setShippingExcelSort(user.id, value));
    sortSaveChainRef.current = attempt.then(() => undefined, () => undefined);
    try {
      await attempt;
      return true;
    } catch (err) {
      logError('[useMemberPreference] 배송엑셀 정렬 저장 실패:', err);
      if (seq === sortSaveSeqRef.current && sortUserIdRef.current === requestUserId) {
        setLocalShippingSort(prevValue);
      }
      return false;
    }
  }, [user, preferenceStatus, shippingExcelSort]);

  return { recordPaymentMethod, preferenceStatus, setRecordPaymentMethod, shippingExcelSort, setShippingExcelSort };
}
